import Table from '@/components/Table/Table';
import { ReviewMember } from '@/features/centerReview';
import { colors } from '@/lib/colors';
import { MediumText, RegularText } from '@/lib/Text';
import { Row } from '@/lib/utils';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import SvgIcon from '@/components/SvgIcon';
import { overlay } from 'overlay-kit';
import ModalMemberReview from './ModalMemberReview';

type Props = {
    reviewMembers?: ReviewMember[];
    reviewMemberCount?: number;
    totalAnswerCount?: number;
};

const columnHelper = createColumnHelper<ReviewMember>();

function FeedInstructorReviewsCoachMembers({ reviewMembers = [], reviewMemberCount = 0, totalAnswerCount = 0 }: Props) {
    const columns = useMemo(
        () => [
            columnHelper.accessor('memberAlias', {
                id: 'memberAlias',
                header: '회원',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue() || '-'}
                    </RegularText>
                ),
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('memberReviewAverageRating', {
                id: 'memberReviewAverageRating',
                header: '전체 평균',
                cell: (info) => {
                    const rating = info.getValue();
                    return (
                        <Row className="flex items-center justify-center gap-[5px]">
                            <svg
                                className="w-[14px] h-[14px] text-[#3D73DD]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                {rating.toFixed(1)}
                            </RegularText>
                        </Row>
                    );
                },
                size: 120,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('firstClassReviewAverageRating', {
                id: 'firstClassReviewAverageRating',
                header: '첫 회차 평균',
                cell: (info) => {
                    const rating = info.getValue();
                    return (
                        <Row className="flex items-center justify-center gap-[5px]">
                            <svg
                                className="w-[14px] h-[14px] text-[#3D73DD]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                {rating.toFixed(1)}
                            </RegularText>
                        </Row>
                    );
                },
                size: 140,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('middleClassReviewAverageRating', {
                id: 'middleClassReviewAverageRating',
                header: '중간 회차 평균',
                cell: (info) => {
                    const rating = info.getValue();
                    return (
                        <Row className="flex items-center justify-center gap-[5px]">
                            <svg
                                className="w-[14px] h-[14px] text-[#3D73DD]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                {rating.toFixed(1)}
                            </RegularText>
                        </Row>
                    );
                },
                size: 140,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('lastClassReviewAverageRating', {
                id: 'lastClassReviewAverageRating',
                header: '마지막 전 회차 평균',
                cell: (info) => {
                    const rating = info.getValue();
                    return (
                        <Row className="flex items-center justify-center gap-[5px]">
                            <svg
                                className="w-[14px] h-[14px] text-[#3D73DD]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                {rating.toFixed(1)}
                            </RegularText>
                        </Row>
                    );
                },
                size: 160,
                enableSorting: true,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('memberAnswerCount', {
                id: 'memberAnswerCount',
                header: '남긴 의견 수',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue()}
                    </RegularText>
                ),
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
            columnHelper.accessor('needsAnswerCount', {
                id: 'needsAnswerCount',
                header: '맞춤 요청 수',
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                        {info.getValue()}
                    </RegularText>
                ),
                size: 120,
                enableSorting: false,
                meta: {
                    align: 'center',
                },
            }),
        ],
        [],
    );

    return (
        <div className="flex w-full flex-col bg-[#FFFFFF] border-[0.5px] border-[#E8E8E8] px-[29px] pt-[38px] rounded-[25px]">
            <div className="mb-[18px]">
                <Row className="flex items-center mb-[36px]">
                    <RegularText fontSize={20} lineHeight={26} color={'#191F28'}>
                        총 {reviewMemberCount.toLocaleString('ko-KR')}명 참여 /{' '}
                        {totalAnswerCount.toLocaleString('ko-KR')}개 의견
                    </RegularText>
                </Row>
            </div>
            <Table
                data={reviewMembers}
                columns={columns}
                rowHeight={84}
                headerColor="#FFFFFF"
                isUnballancedRowColor
                rowRadius={14}
                emptyText="참여한 회원이 없습니다."
                isPagination
                onRowClick={(row: ReviewMember) => {
                    // TODO : 나중에 reviewId 로 바꿔놓기
                    overlay.open(({ unmount }) => (
                        <ModalMemberReview unmount={unmount} reviewId={row.reviewId || ''} />
                    ));
                }}
                customPaginationComponent={({
                    nextPage,
                    canNextPage,
                    previousPage,
                    canPreviousPage,
                    pageIndex,
                    totalPageIndex,
                    setPageIndex,
                }) => {
                    const totalPages = totalPageIndex || 0;
                    const currentPage = (pageIndex || 0) + 1;

                    // Calculate visible page range (groups of 5: 1-5, 6-10, 11-15, etc.)
                    const pageGroup = Math.ceil(currentPage / 5);
                    const startPage = (pageGroup - 1) * 5 + 1;
                    const endPage = Math.min(pageGroup * 5, totalPages);
                    const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

                    return (
                        <Row className="flex items-center self-center my-[22px]">
                            <Row className="flex items-center gap-[12px] mr-[30px]">
                                {visiblePages.map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        onClick={() => setPageIndex?.(pageNum - 1)}
                                        className={`w-[32px] h-[32px] rounded-full flex items-center justify-center ${
                                            currentPage === pageNum ? 'bg-[#3D4244]' : 'hover:bg-[#F0F0F0]'
                                        }`}
                                    >
                                        <MediumText
                                            fontSize={16}
                                            lineHeight={21}
                                            color={currentPage === pageNum ? '#FFFFFF' : colors.Text_1}
                                        >
                                            {pageNum}
                                        </MediumText>
                                    </button>
                                ))}
                            </Row>
                            <button
                                onClick={previousPage}
                                disabled={canPreviousPage}
                                className="disabled:opacity-30 disabled:cursor-not-allowed mr-[37px]"
                            >
                                <SvgIcon
                                    name={'SvgLeftStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>

                            <button
                                onClick={nextPage}
                                disabled={canNextPage}
                                className="disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <SvgIcon
                                    name={'SvgRightStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    );
                }}
            />
        </div>
    );
}

export default FeedInstructorReviewsCoachMembers;
